/**
 * Risk Visibility Helper (frontend)
 *
 * Risk data is for underwriting personas only (internal admins, insurer /
 * underwriter partners, approved analysts). Ordinary merchant/client
 * shipping users must NOT see risk score, band, drivers, confidence,
 * recommendation, supporting signals, or any UI affordance hinting that
 * risk exists.
 *
 * IMPORTANT: this helper is the single source of truth for the frontend.
 * Use it from Shipping rows, sheets, panels, KPI strips, and any future
 * risk surface. Do NOT roll your own check.
 *
 * The backend mirror lives at
 * `payhelm/papaya/src/api/middleware/riskAccess.ts`. Frontend hiding alone
 * is not sufficient - server enforcement is the actual security boundary.
 *
 * Persona writeup: `payhelm/docs/shipping-audit/07-risk-visibility-access-model.md`.
 */

import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  applyDevPersonaToSessionPermissions,
  getDevPersonaSessionOverride,
  isDevPermissionPersonaEnabled,
} from '../modules/devPermissionPersona'
import { PERMISSIONS } from './Router.permissionRoute'

export type RiskCapability =
  | typeof PERMISSIONS.RISK_VIEW
  | typeof PERMISSIONS.RISK_ORDER_DETAIL
  | typeof PERMISSIONS.RISK_MERCHANT_PROFILE

export interface RiskAccessContext {
  admin?: boolean | null
  permissions?: string[] | null
}

/**
 * Pure check. Use this when you already have permissions in scope (props,
 * unit tests, selectors). Admins always pass.
 */
export const canViewRisk = (
  ctx: RiskAccessContext,
  capability: RiskCapability = PERMISSIONS.RISK_VIEW,
): boolean => {
  if (ctx?.admin) return true
  const perms = ctx?.permissions || []
  return perms.includes(capability)
}

/**
 * Same check, but accepts the underlying RBAC list directly. Convenient
 * for legacy `connect`-style components that already destructure
 * `permissions` and `admin` separately.
 */
export const canViewRiskFromState = (
  admin: boolean | null | undefined,
  permissions: string[] | null | undefined,
  capability: RiskCapability = PERMISSIONS.RISK_VIEW,
): boolean => canViewRisk({ admin: !!admin, permissions: permissions || [] }, capability)

/**
 * Effective admin + permissions for RBAC checks. When a dev persona is active
 * (localStorage + `?devPersona=`), persona permissions win and merchant personas
 * force `admin: false` so risk hooks do not treat the session as internal admin.
 */
export const resolveEffectiveAccess = (
  reduxAdmin: boolean,
  reduxPermissions: string[],
): { admin: boolean; permissions: string[] } => {
  const override = getDevPersonaSessionOverride()
  if (override) {
    return { admin: override.admin, permissions: override.permissions }
  }
  if (isDevPermissionPersonaEnabled()) {
    const applied = applyDevPersonaToSessionPermissions(reduxPermissions, reduxAdmin)
    if (applied.active) {
      return { admin: applied.admin, permissions: applied.permissions }
    }
  }
  return { admin: !!reduxAdmin, permissions: reduxPermissions || [] }
}

export const resolveRiskCapabilities = (
  reduxAdmin: boolean,
  reduxPermissions: string[],
): {
  canViewRisk: boolean
  canViewOrderRisk: boolean
  canViewMerchantRisk: boolean
} => {
  const { admin, permissions } = resolveEffectiveAccess(reduxAdmin, reduxPermissions)
  return {
    canViewRisk: canViewRiskFromState(admin, permissions, PERMISSIONS.RISK_VIEW),
    canViewOrderRisk: canViewRiskFromState(admin, permissions, PERMISSIONS.RISK_ORDER_DETAIL),
    canViewMerchantRisk: canViewRiskFromState(admin, permissions, PERMISSIONS.RISK_MERCHANT_PROFILE),
  }
}

/**
 * Hook for functional components. Returns `true` if the current user has
 * the requested risk capability (or is an admin).
 *
 * Usage:
 *   const canSeeRisk = useCanViewRisk()
 *   const canDrillIntoOrder = useCanViewRisk(PERMISSIONS.RISK_ORDER_DETAIL)
 *   if (!canSeeRisk) return null
 */
export const useCanViewRisk = (
  capability: RiskCapability = PERMISSIONS.RISK_VIEW,
): boolean => {
  const reduxAdmin = useSelector((state: any) => !!state.root?.admin)
  const reduxPermissions: string[] = useSelector(
    (state: any) => state.root?.permissions || [],
  )
  const { admin, permissions } = useMemo(
    () => resolveEffectiveAccess(reduxAdmin, reduxPermissions),
    [reduxAdmin, reduxPermissions],
  )
  return canViewRiskFromState(admin, permissions, capability)
}

/**
 * Hook returning all three capability flags in one shot. Useful for
 * components that need to render different sections based on different
 * capabilities (e.g. show order signal but not merchant profile).
 */
export const useRiskCapabilities = (): {
  canViewRisk: boolean
  canViewOrderRisk: boolean
  canViewMerchantRisk: boolean
} => {
  const reduxAdmin = useSelector((state: any) => !!state.root?.admin)
  const reduxPermissions: string[] = useSelector(
    (state: any) => state.root?.permissions || [],
  )
  return useMemo(
    () => resolveRiskCapabilities(reduxAdmin, reduxPermissions),
    [reduxAdmin, reduxPermissions],
  )
}

export default canViewRisk
